import { yupResolver } from "@hookform/resolvers/yup";
import { t } from "i18next";
import { useCallback, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";

import { getDataFromResponse } from "~/api/axiosUtils.ts";
import Button from "~/components/buttons/Button";
import DatePickerInput from "~/components/formElements/DatePickerInput";
import FormHasErrorsHint from "~/components/formElements/FormHasErrorsHint";
import Input from "~/components/formElements/Input";
import RestrictedNumberInput from "~/components/formElements/RestrictedNumberInput";
import SubmitButton from "~/components/formElements/SubmitButton";
import Textarea from "~/components/formElements/Textarea";
import Headline from "~/components/Headline";
import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarErrorOverlay from "~/components/Sidebar/components/SidebarErrorOverlay";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import { updateDraftInvoiceStatusToSent } from "~/modules/billing/api/draftInvoice/draftInvoiceApiDispatchers.ts";
import InvoiceTotalsFormSection from "~/modules/billing/components/InvoiceTotalsFormSection";
import type { UnprocessedInvoicesIndexTableData } from "~/modules/billing/components/unprocessedInvoicesTableTypes.ts";
import { DEFAULT_PAYMENT_TARGET_DAYS, DEFAULT_VAT_PERCENTAGE } from "~/modules/billing/constants/billingConstants.ts";
import { formatDateToYYYYMMDD } from "~/utils/dateAndTimeUtils.ts";
import { preventSubmitOnEnter } from "~/utils/form/formUtils.ts";

type FormData = {
	comment: string | null;
	invoiceSentAt: Date;
	paymentTargetDays: number;
	recipientInformation: string | null;
	subtotalCents: number;
	travelCostCentsBeforeVat: number;
	vatPercentage: number;
}

type Props = {
	onCancel: () => void;
	onSuccess: (newInvoiceId: string) => void;
	draftInvoiceData: UnprocessedInvoicesIndexTableData;
};

const today = new Date();

const FinishBillingProcessOfProjectInvoiceForm = ({
	onCancel,
	onSuccess,
	draftInvoiceData,
}: Props) => {
	const [isBusy, setIsBusy] = useState(false);
	const [serverErrorMessage, setServerErrorMessage] = useState("");

	const schema = useMemo(() => {
		return yup.object({
			comment: yup.string().nullable().default(""),
			invoiceSentAt: yup.date().required(),
			paymentTargetDays: yup.number().required(),
			recipientInformation: yup.string().nullable().default(""),
			subtotalCents: yup.number().required(),
			travelCostCentsBeforeVat: yup.number().required(),
			vatPercentage: yup.number().required(),
		});
	}, []);
	const defaultValues = useMemo(() => {
		return {
			comment: draftInvoiceData.comment || "",
			invoiceSentAt: new Date(),
			paymentTargetDays: draftInvoiceData.paymentTargetDays || DEFAULT_PAYMENT_TARGET_DAYS,
			recipientInformation: "",
			subtotalCents: draftInvoiceData.invoiceTotalCents,
			travelCostCentsBeforeVat: 0,
			vatPercentage: DEFAULT_VAT_PERCENTAGE,
		};
	}, [draftInvoiceData.comment, draftInvoiceData.invoiceTotalCents, draftInvoiceData.paymentTargetDays]);

	const {
		handleSubmit,
		control,
		formState: { isSubmitted, isValid },
		watch,
	} = useForm<FormData>({
		defaultValues: defaultValues,
		resolver: yupResolver<FormData>(schema),
		mode: "onChange",
	});

	const onSubmit = useCallback(async (data: FormData) => {
		try {
			setIsBusy(true);
			const updateData = {
				...data,
				invoiceSentAt: formatDateToYYYYMMDD(data.invoiceSentAt),
			};

			const res = await updateDraftInvoiceStatusToSent(draftInvoiceData.id, updateData);
			const newInvoiceId = getDataFromResponse(res);
			onSuccess(newInvoiceId);
		} catch (error) {
			console.log(error);
			setServerErrorMessage(t("form:unexpectedError"));
			setIsBusy(false);
		}
	}, [draftInvoiceData.id, onSuccess]);

	const formIsSubmittable = isValid && !isBusy;

	return (
		<form onSubmit={handleSubmit(onSubmit)}
			  onKeyDown={preventSubmitOnEnter}
			  className="auto-grid-sidebar"
		>
			<SidebarHeader>
				<SidebarHeaderHeadline>Rechnungsvorgang abschliessen</SidebarHeaderHeadline>
			</SidebarHeader>
			<div className="grow gap-x-8 overflow-auto px-7 py-2">
				{isBusy && <SidebarBusyOverlay />}
				{serverErrorMessage &&
					<SidebarErrorOverlay title="Update fehlgeschlagen">{serverErrorMessage}</SidebarErrorOverlay>}

				<div className="grid h-auto grid-cols-2 gap-4 pt-1">
					<div className="col-span-2">
						<Headline className="mb-2"
								  type="h4">Empfänger / Zahlungsziel</Headline>
						<Input control={control}
							   label="Versendet an"
							   name="recipientInformation"
							   placeholder="Name, Versandart, ggf. E-Mail-Adresse"
						/>
					</div>
					<DatePickerInput name="invoiceSentAt"
									 label="Versendet am / Rechnungsdatum"
									 control={control}
									 minDate={today} />
					<RestrictedNumberInput control={control}
										   minValue={0}
										   maxValue={999999}
										   label="Zahlungsziel in Tagen"
										   name="paymentTargetDays"
										   placeholder="Anzahl Tage"
					/>
					<InvoiceTotalsFormSection<FormData> control={control}
														watch={watch}
														title="Rechnungsbetrag" />
					<div className="col-span-2 mt-4">
						<Headline className="mb-2"
								  type="h4">Kommentar</Headline>
						<Textarea control={control}
								  name="comment"
								  placeholder="Hier können Besonderheiten zum Abrechnungsprozess vermerkt werden."
						/>
					</div>
					<div className="col-span-2 mt-4">
						<Headline className="mb-2"
								  type="h4">Rechnungsnummer</Headline>
						<p className="leading-tight text-gray-800">Die Rechnungsnummer wird beim Absenden dieses
							Formulars automatisch erzeugt und muss anschliessend im Rechnungsdokument nachgetragen
							werden.</p>
					</div>
				</div>
			</div>
			<SidebarFooter>
				<FormHasErrorsHint show={isSubmitted && !isValid}
								   className="mr-2" />
				<SubmitButton busy={isBusy}
							  disabled={!formIsSubmittable}>
					Rechnungsstatus in &quot;Versendet&quot; ändern
				</SubmitButton>
				<Button theme="white"
						onClick={onCancel}>
					Abbrechen
				</Button>
			</SidebarFooter>
		</form>);
};

export default FinishBillingProcessOfProjectInvoiceForm;
